import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';
import { useParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';

// Function
import { getUTCTime } from '../../functions/hreadableDate';

// Components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faMoneyBillAlt, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { Button, Row, Col } from 'react-bootstrap';
import Order from './Order';
import StatusBadge from '../../components/StatusBadge';
import StatusBox from './StatusBox';
import StatusChangeModal from './StatusChangeModal';
import PaymentModal from './PaymentModal';
import ReceiptModal from '../../components/ReceiptModal';
import AuthModal from '../../components/AuthModal';
import CreateRepairModal from '../RepairScreen/CreateRepairModal';

// Constants
import {
    ORDER_DELIVERED,
    ORDER_UPDATE_VALIDATION_ERROR,
    ORDER_NOT_FOUND,
    USER_NOT_FOUND,
    ORDER_CANCELED,
    CANT_RESTATUS_ORDER,
    ORDER_NOT_PAID
} from '../../constants/status';
const MAX_COMMENTS_PER_PAGE = 3;

const AUTH_ACTION = {
    CREATE_COMMENT: 'CREATE_COMMENT',
    CHANGE_STATUS: 'CHANGE_STATUS',
    MAKE_PAYMENT: 'MAKE_PAYMENT',
    UPDATE_ORDER: 'UPDATE_ORDER'
};

const OrderScreen = (props) => {
    const { orderId } = useParams();
    const [showChangeStatus, setShowChangeStatus] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [showAuth, setShowAuth] = useState(false);
    const [authOptions, setAuthOptions] = useState(null);
    const [order, setOrder] = useState(null);
    const [loadingStatusBox, setLoadingStatusBox] = useState(false);
    const [commentsPagination, setCommentsPagination] = useState({
        current: 1,
        total: 1,
        items_per_page: MAX_COMMENTS_PER_PAGE
    });
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        const loadOrder = async () => {
            const url = process.env.REACT_APP_API_URL;
            const authToken = sessionStorage.getItem('auth-token');

            const request = await fetch(`${url}/repairs/${orderId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    }
                });

            if (request.status === 200) {
                const fetchedOrder = await request.json();
                setOrder(fetchedOrder);
                setCommentsPagination(oldComment => ({
                    ...oldComment,
                    current: 1,
                    total: Math.ceil(fetchedOrder.comments.length / MAX_COMMENTS_PER_PAGE)
                }));
            }
        };

        loadOrder();
    }, [orderId]);

    // Send a New Comment Handler
    const sendNewComment = async (comment, cb = () => true) => {
        setLoadingStatusBox(true); // Show Loading Spinner
        const url = process.env.REACT_APP_API_URL;
        const authToken = sessionStorage.getItem('auth-token');
        const user = jwtDecode(authToken);

        const request = await fetch(`${url}/repairs/order/${order._id}/comment`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    userId: user.id,
                    comment: comment
                })
            });

        if (request.status === 201) {
            const fetchedComments = await request.json();

            // Update comments
            setOrder({
                ...order,
                comments: fetchedComments.comments
            });

            // Run callback when successful
            cb();
        }

        setLoadingStatusBox(false); // Remove Loading Spinner
    };

    // Change Status Handler
    const changeStatusHandler = async (status, options = {}, cb) => {
        const url = process.env.REACT_APP_API_URL;
        const authToken = sessionStorage.getItem('auth-token');
        const user = jwtDecode(authToken);

        const request = await fetch(`${url}/repairs/order/${order._id}/status`,
            {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    userId: user.id,
                    status: status,
                    ...options
                })
            });

        if (request.status === 200) {
            // Update the order and reflect changes
            const updatedOrder = await request.json();
            setOrder(updatedOrder);

            // Close The Modal when Successful
            setShowChangeStatus(false);
        } else {
            const response = await request.json();

            switch (response.status) {
                case ORDER_DELIVERED:
                    return cb("La orden ha sido entregada al cliente.");
                case ORDER_UPDATE_VALIDATION_ERROR:
                    return cb("Problema al validar el cambio de estado.");
                case ORDER_NOT_FOUND:
                    return cb("No se encotró la orden.");
                case USER_NOT_FOUND:
                    return cb("No se encotró el usuario.");
                case ORDER_CANCELED:
                    return cb("La orden fue cancelada.");
                case CANT_RESTATUS_ORDER:
                    return cb("No se puede duplicar el mismo estado.");
                case ORDER_NOT_PAID:
                    return cb("La orden no ha sido pagada aun.");

                default:
                    return cb("Estado desconocido. comuniquese con el administrador");
            }
        }
    };

    // Assign Page of pagination
    const changeCommentPage = (pageNumber, total = false) => {

        if (!total)
            setCommentsPagination({
                ...commentsPagination,
                current: pageNumber
            });
        else
            setCommentsPagination({
                ...commentsPagination,
                total: total,
                current: pageNumber
            });
    };

    const makePayment = async (payment) => {
        const url = process.env.REACT_APP_API_URL;
        const authToken = sessionStorage.getItem('auth-token');
        const user = jwtDecode(authToken);

        if (!isNaN(parseFloat(payment))) {
            setShowPayment(false);
            const request = await fetch(`${url}/repairs/order/${order._id}/pay`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        userId: user.id,
                        paid: +payment
                    })
                });

            switch (request.status) {
                case 200:
                    // Reflect changes in the order
                    const updatedOrder = await request.json();
                    setOrder(updatedOrder);

                    // Show Receipt When Successful
                    setShowReceipt(true);
                    break;
                case 500:
                    // setErrorMessage('Hubo un error en el servidor.')
                    break;
                default:
            }
        } else {
            // setErrorMessage('El valor a pagar no es un numero valido.')
        }
    }

    const updateOrder = async ({state}) => {
        const url = process.env.REACT_APP_API_URL;
        const authToken = sessionStorage.getItem('auth-token');
        const userId = jwtDecode(authToken).id;

        // Data to be sent
        const postData = {
            service: state.service.value,
            client: {
                name: state.customer.value,
                phone: state.phone.value,
            },
            device: {
                name: state.device.value,
                password: state.password.value
            },
            total: +state.total.value,
            paid: +state.deposit.value,
            notes: state.notes.value,
            userId: userId
        };

        const request = await fetch(`${url}/repairs/order/${order._id}`,
            {
                method: 'PATCH',
                body: JSON.stringify(postData),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                }
            });

        if (request.status === 200) {
            const order = await request.json();

            // Update the Order
            setShowEditModal(false);
            setOrder(order);
            setShowReceipt(true);
            
            return true;
        } else {
            const response = await request.json();
        }

        return false;
    }

    // Auth Function
    const authFunction = async () => {
        switch (authOptions.type) {
            case AUTH_ACTION.CREATE_COMMENT:
                sendNewComment(authOptions.comment, authOptions.cb);
                break;
            case AUTH_ACTION.CHANGE_STATUS:
                changeStatusHandler(authOptions.status, {
                    ...authOptions
                }, authOptions.cb);
                break;
            case AUTH_ACTION.MAKE_PAYMENT:
                await makePayment(authOptions.payment);
                break;
            case AUTH_ACTION.UPDATE_ORDER:
                await updateOrder(authOptions);
                break;
            default:
                console.debug("Default Action");
        }
    }

    // Show Auth Box
    const startAuthentication = (action, options = {}, cb = () => null) => {
        setShowAuth(true);
        setAuthOptions({
            ...options,
            type: action,
            cb: cb
        });
    }

    return (
        <section className={styles.OrderScreen}>
            <div className={styles.Heading}>
                <h4>Orden #{orderId} {order && <StatusBadge status={order.status} />} {order && order.status === 'ordered' && <StatusBadge status="time" time={`Llega ${getUTCTime(order.deliveryDate, true)}`} />}</h4>
                <div>
                    <Button
                        variant="dark"
                        className={styles.Button}
                        onClick={() => setShowEditModal(true)}>
                        <FontAwesomeIcon className={styles.Icon} icon={faPencilAlt} />
                        <span>Editar Orden</span>
                    </Button>
                    <Button
                        variant="warning"
                        className={styles.Button}
                        onClick={() => setShowChangeStatus(true)}>
                        <FontAwesomeIcon className={styles.Icon} icon={faSync} />
                        <span>Cambiar Estado</span>
                    </Button>
                    <Button
                        variant="primary"
                        className={styles.Button}
                        onClick={() => setShowPayment(true)}>
                        <FontAwesomeIcon className={styles.Icon} icon={faMoneyBillAlt} />
                        <span>Hacer Pago</span>
                    </Button>
                </div>
            </div>
            <Row>
                <Col xs={8}>
                    <Order order={order} />
                </Col>
                <Col>
                    <StatusBox
                        orderId={order ? order._id : null}
                        comments={order ? order.comments : []}
                        sendComment={startAuthentication}
                        pagination={commentsPagination}
                        setPage={changeCommentPage}
                        loading={loadingStatusBox} />
                </Col>
            </Row>
            <StatusChangeModal
                show={showChangeStatus && !showAuth}
                onClose={() => setShowChangeStatus(false)}
                changeStatus={startAuthentication} />
            <PaymentModal
                total={order ? order.total : 0}
                paid={order ? order.paid : 0}
                orderId={order ? order._id : null}
                show={showPayment && !showAuth}
                updateOrder={setOrder}
                makePayment={startAuthentication}
                showReceipt={() => setShowReceipt(true)}
                onClose={() => setShowPayment(false)} />
            <ReceiptModal
                show={showReceipt}
                onClose={() => setShowReceipt(false)}
                order={order} />
            <AuthModal
                show={showAuth}
                userData={props.userData}
                updateUserData={props.updateUserData}
                func={authFunction}
                hide={() => setShowAuth(false)}
            />
            <CreateRepairModal
                key="EditCreateModal"
                show={showEditModal && !showAuth}
                order={order}
                onHide={() => setShowEditModal(false)}
                updateRepair={startAuthentication}
                editMode
                />
        </section>
    );
}

export default OrderScreen;